import CardActions from "@components/HomePage/CardParts/CardActions";
import CardDashedBorder from "@components/HomePage/CardParts/CardDashedBorder";
import CardNotLoadedActions from "@components/HomePage/CardParts/CardNotLoadedActions";
import type { CardState } from "@components/HomePage/CardParts/getCardState";
import { classNames } from "@components/libs/classNames";
import type { CatalogLogo } from "@core-ui/ContextServices/CatalogLogoService/catalogLogoHooks";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import t from "@ext/localization/locale/translate";
import { type CatalogLink, isCloningStatus, isNotLoadedStatus } from "@ext/navigation/NavigationLinks";
import { ActionCard, Card, CardFooter, CardSubTitle, CardTitle } from "@ui-kit/Card";
import { OverflowTooltip } from "@ui-kit/Tooltip";
import type { ReactNode } from "react";
import { CardLogo } from "./CardParts/CardLogo";

export interface CardViewProps {
	state: CardState;
	link: CatalogLink;
	className?: string;
	logo?: CatalogLogo;
	footer?: ReactNode;
	isReadOnly?: boolean;
	onCardClick?: () => void;
	onErrorClick?: () => void;
}

const errorCardClassName =
	"border-status-error-secondary-border bg-secondary-bg hover:border-status-error-secondary-border hover:bg-status-error-bg";
const notLoadedCardClassName = "border-0 bg-transparent !shadow-none";
const notLoadedLogoClassName = "grayscale opacity-40";

const withActions: CardState[] = ["ready", "unpublished", "broken"];

const CardView = (props: CardViewProps) => {
	const { state, link, className, logo, footer, isReadOnly, onCardClick, onErrorClick } = props;
	const { isNext, isStatic, isDocportal } = usePlatform();
	const isPlaceholder = isNotLoadedStatus(state);
	const isFailed = state === "clone-error" || state === "clone-failed";
	const renderLogo = !!logo && !isCloningStatus(state) && state !== "clone-error";
	const CardRoot = isPlaceholder ? Card : ActionCard;

	const onClick = () => {
		if (isReadOnly) return;
		if (state === "clone-error") return onErrorClick?.();
		if (isNext || isStatic || isDocportal || isCloningStatus(state)) return;
		onCardClick();
	};

	return (
		<CardRoot
			className={classNames(
				"h-[132px] relative",
				{ [errorCardClassName]: isFailed, [notLoadedCardClassName]: isPlaceholder },
				[className],
			)}
			data-card="true"
			onClick={isPlaceholder ? undefined : onClick}
			{...(isPlaceholder && { role: "group", "aria-label": link.title })}
			onKeyDown={null}
			style={
				state !== "clone-error" && !isPlaceholder && link.style
					? { background: `var(--color-card-bg-${link.style})` }
					: undefined
			}
		>
			<CardTitle>
				<OverflowTooltip className="line-clamp-2">{link.title || t("article.no-name")}</OverflowTooltip>
			</CardTitle>
			<CardSubTitle>
				<OverflowTooltip className={classNames("line-clamp-2", { "pr-14": renderLogo }, [])}>
					{link.description}
				</OverflowTooltip>
			</CardSubTitle>
			{!isReadOnly && withActions.includes(state) && <CardActions catalogLink={link} />}
			{!isReadOnly && state === "not-loaded" && <CardNotLoadedActions catalogLink={link} />}
			<CardFooter className={`flex ${renderLogo ? "mr-14" : ""}`}>{footer}</CardFooter>

			{isPlaceholder && <CardDashedBorder />}
			{renderLogo && <CardLogo className={isPlaceholder ? notLoadedLogoClassName : undefined} logo={logo} />}
		</CardRoot>
	);
};

export default CardView;
